/**
 * useAddWorkflowState Hook
 * Persists Add page workflow state to sessionStorage.
 * Allows workflow to resume after navigating away (e.g., to create a template).
 * State is only cleared on successful job submission or explicit reset.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { CreateStep, InputMethod, ProjectConfig } from '@/lib/types';
import type { TemplateMetadata } from '@/services/templatesApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAddWorkflowState');
const STORAGE_KEY = 'create_workflow_state';
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

export interface AddWorkflowState {
  step: CreateStep;
  uploadedS3Key: string;
  uploadId: string;
  inputMethod: InputMethod;
  note: string;
  selectedProjectId: string | null;
  selectedTemplateId: string | null;
  timestamp: number;
  sessionId?: string | null;
  transcriptionMode?: 'batch' | 'streaming';
}

interface UseAddWorkflowStateProps {
  step: CreateStep;
  uploadedS3Key: string;
  uploadId: string;
  inputMethod: InputMethod;
  note: string;
  selectedProject: ProjectConfig | null;
  selectedTemplate: TemplateMetadata | null;
  setStep: (step: CreateStep) => void;
  setUploadedS3Key: (key: string) => void;
  setUploadId: (id: string) => void;
  setInputMethod: (method: InputMethod) => void;
  setNote: (content: string) => void;
  setSelectedProject: (project: ProjectConfig | null) => void;
  setSelectedTemplate: (template: TemplateMetadata | null) => void;
  projects: ProjectConfig[];
  templates: TemplateMetadata[];
  sessionId?: string | null;
  transcriptionMode?: 'batch' | 'streaming';
}

interface UseAddWorkflowStateReturn {
  saveState: () => void;
  clearState: () => void;
  hasRestoredState: boolean;
}

export function useAddWorkflowState({
  step,
  uploadedS3Key,
  uploadId,
  inputMethod,
  note,
  selectedProject,
  selectedTemplate,
  setStep,
  setUploadedS3Key,
  setUploadId,
  setInputMethod,
  setNote,
  setSelectedProject,
  setSelectedTemplate,
  projects,
  templates,
  sessionId,
  transcriptionMode,
}: UseAddWorkflowStateProps): UseAddWorkflowStateReturn {
  const hasRestoredRef = useRef(false);
  const isInitializedRef = useRef(false);

  // Save state to sessionStorage
  const saveState = useCallback(() => {
    // Don't save if we're at capture step (nothing to save)
    if (step === 'capture') {
      return;
    }

    try {
      const state: AddWorkflowState = {
        step,
        uploadedS3Key,
        uploadId,
        inputMethod,
        note,
        selectedProjectId: selectedProject?.id || null,
        selectedTemplateId: selectedTemplate?.id || null,
        timestamp: Date.now(),
        sessionId: sessionId || null,
        transcriptionMode: transcriptionMode || 'batch',
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      log.debug('Workflow state saved', { step, inputMethod, transcriptionMode });
    } catch (err) {
      log.warn('Failed to save workflow state', { error: err instanceof Error ? err.message : 'Unknown' });
    }
  }, [step, uploadedS3Key, uploadId, inputMethod, note, selectedProject, selectedTemplate, sessionId, transcriptionMode]);

  // Clear state from sessionStorage
  const clearState = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      log.info('Workflow state cleared');
    } catch (err) {
      log.warn('Failed to clear workflow state', { error: err instanceof Error ? err.message : 'Unknown' });
    }
  }, []);

  // Load state from sessionStorage
  const loadState = useCallback((): AddWorkflowState | null => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved) as AddWorkflowState;
        // Check if expired
        if (Date.now() - state.timestamp > EXPIRY_MS) {
          clearState();
          log.info('Workflow state expired, cleared');
          return null;
        }
        return state;
      }
    } catch (err) {
      log.warn('Failed to load workflow state', { error: err instanceof Error ? err.message : 'Unknown' });
    }
    return null;
  }, [clearState]);

  // Restore state on mount (once projects and templates are loaded)
  useEffect(() => {
    // Only restore once
    if (hasRestoredRef.current || isInitializedRef.current) {
      return;
    }

    // Wait for projects to load (templates can be empty for skipped template case)
    if (projects.length === 0 && step === 'capture') {
      // Projects not loaded yet, wait
      return;
    }

    isInitializedRef.current = true;

    const savedState = loadState();
    if (!savedState) {
      return;
    }

    // Determine input category from saved state
    const savedInputMethod = savedState.inputMethod || 'voice';
    const isAudioInput = savedInputMethod === 'voice' || savedInputMethod === 'upload-recording';
    const isStreamingSaved = savedState.transcriptionMode === 'streaming' && savedInputMethod === 'voice';

    // Streaming sessions are recovered via the interrupted sessions banner, not workflow state
    if (isStreamingSaved && savedState.sessionId) {
      log.info('Streaming session found in saved state — will be recovered via interrupted sessions');
      clearState();
      return;
    }

    // Validate saved state based on input category
    if (isAudioInput) {
      if (!savedState.uploadedS3Key || !savedState.uploadId) {
        log.warn('Invalid saved state (audio input missing S3 key or upload ID), clearing');
        clearState();
        return;
      }
    } else {
      if (!savedState.note?.trim()) {
        log.warn('Invalid saved state (text input missing content), clearing');
        clearState();
        return;
      }
    }

    // Restore input method and content
    setInputMethod(savedInputMethod);
    setUploadedS3Key(savedState.uploadedS3Key || '');
    setUploadId(savedState.uploadId || '');
    setNote(savedState.note || '');

    // Restore project if we have one
    if (savedState.selectedProjectId) {
      const project = projects.find(p => p.id === savedState.selectedProjectId);
      if (project) {
        setSelectedProject(project);
      } else {
        // Project not found (deleted?), go to project selection
        setStep('project');
        hasRestoredRef.current = true;
        log.info('Restored workflow state (project not found, going to project selection)', { step: 'project' });
        return;
      }
    }

    // Restore template if we have one
    if (savedState.selectedTemplateId) {
      const template = templates.find(t => t.id === savedState.selectedTemplateId);
      if (template) {
        setSelectedTemplate(template);
      }
      // If template not found, user can select again
    }

    // Restore step
    setStep(savedState.step);
    hasRestoredRef.current = true;

    log.info('Restored workflow state', { step: savedState.step, inputMethod: savedInputMethod, hasProject: !!savedState.selectedProjectId });
  }, [
    projects,
    templates,
    step,
    loadState,
    clearState,
    setStep,
    setUploadedS3Key,
    setUploadId,
    setInputMethod,
    setNote,
    setSelectedProject,
    setSelectedTemplate,
  ]);

  // Auto-save state when it changes (after initial restore)
  useEffect(() => {
    if (hasRestoredRef.current || isInitializedRef.current) {
      saveState();
    }
  }, [step, uploadedS3Key, uploadId, inputMethod, note, selectedProject, selectedTemplate, sessionId, transcriptionMode, saveState]);

  return {
    saveState,
    clearState,
    hasRestoredState: hasRestoredRef.current,
  };
}
